import React from 'react';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { formatNoticeDate } from 'modules/v2/utils/subscriptionNotice';

export type FlowOutcome =
  | { kind: 'planChangeScheduled'; planName: string | null; effectiveAt: string | null }
  | { kind: 'canceled'; endsAt: string | null };

interface FlowOutcomeModalProps {
  outcome: FlowOutcome;
  onClose: () => void;
}

/**
 * Confirms what the member just did in the ProsperStack flow, which closes
 * without a confirmation of its own: a downgrade scheduled for the end of the
 * term, or a cancellation.
 */
const FlowOutcomeModal: React.FC<FlowOutcomeModalProps> = ({ outcome, onClose }) => {
  const isPlanChange = outcome.kind === 'planChangeScheduled';
  const date = isPlanChange ? outcome.effectiveAt : outcome.endsAt;
  const formattedDate = date ? formatNoticeDate(date) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="flow-outcome-title"
        className="bg-white rounded-lg max-w-md w-full shadow-xl overflow-hidden"
      >
        <div className={`h-2 ${isPlanChange ? 'bg-success-500' : 'bg-error-500'}`} />
        <div className="p-6">
          <h3 id="flow-outcome-title" className="text-xl font-bold text-neutral-800 mb-3">
            {isPlanChange ? 'Your plan change is scheduled' : 'Your cancellation is confirmed'}
          </h3>
          {isPlanChange ? (
            <p className="text-neutral-700 leading-relaxed">
              You will move to{' '}
              <span className="font-semibold">{outcome.planName || 'your new plan'}</span>
              {formattedDate ? (
                <>
                  {' '}on <span className="font-semibold">{formattedDate}</span>, at the end of your
                  current billing period
                </>
              ) : (
                <> at the end of your current billing period</>
              )}
              . Until then you keep everything in your current plan.
            </p>
          ) : (
            <p className="text-neutral-700 leading-relaxed">
              {formattedDate ? (
                <>
                  Your membership stays active until{' '}
                  <span className="font-semibold">{formattedDate}</span>. After that date you will
                  lose access to your books, leads and credits. Changed your mind? You can keep
                  your membership from this page before that date.
                </>
              ) : (
                <>Your membership has been canceled.</>
              )}
            </p>
          )}
          <p className="text-sm text-neutral-500 mt-3">
            Questions? Email us at{' '}
            <a href="mailto:support@authorify.com" className="text-blue-600 hover:underline">
              support@authorify.com
            </a>
            .
          </p>
          <div className="flex justify-end mt-6">
            <Button type="primary" onClick={onClose}>
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowOutcomeModal;
